import { createAsyncThunk } from "@reduxjs/toolkit";
import { environment } from "../../environments";
import {
  GetCustomFieldDTORequst,
  GetCustomFieldDTOResponse,
  GetBaseCustomFieldDTORequst,
  GetBaseCustomFieldDTOResponse,
  CreateCustomFieldDTO,
  CreateCustomFieldResponse,
  DeleteCustomFieldResponse,
  DeleteCustomFieldRequst,
  CustomFieldSetValueResponse,
  CustomFieldSetValueRequst,
  UpdateCustomFieldPositionIdxRequest,
  UpdateCustomFieldPositionIdxResponse,
  GetFieldPurposeRequest,
  GetFieldPurposeResponse,
} from "../../models/CustomField.model";

const API_BASE_URL = environment.apiUrl;

export const getCustomField = createAsyncThunk<
  GetCustomFieldDTOResponse,
  GetCustomFieldDTORequst,
  { rejectValue: string }
>("customField/getCustomField", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}CustomField/GetCustomFields`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data: GetCustomFieldDTOResponse = await response.json();

    if (!data.success) {
      return rejectWithValue(data.message || "Failed to fetch custom fields");
    }

    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const getBaseCustomField = createAsyncThunk<
  GetBaseCustomFieldDTOResponse,
  GetBaseCustomFieldDTORequst,
  { rejectValue: string }
>(
  "customField/getBaseCustomField",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}CustomField/GetBaseCustomFields`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: GetBaseCustomFieldDTOResponse = await response.json();

      if (!data.success) {
        return rejectWithValue(
          data.message || "Failed to fetch base custom fields",
        );
      }
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

export const createOrUpdateCustomField = createAsyncThunk<
  CreateCustomFieldResponse,
  CreateCustomFieldDTO,
  { rejectValue: string }
>("customField/createOrUpdate", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}CustomField/CreateOrUpdeteCustomField`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data: CreateCustomFieldResponse = await response.json();

    if (!data.success) {
      return rejectWithValue(data.message || "Failed to save custom field");
    }

    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});
export const SetCustomFieldValue = createAsyncThunk<
  CustomFieldSetValueResponse,
  CustomFieldSetValueRequst,
  { rejectValue: string }
>(
  "customField/setCustomFieldValue",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}CustomField/SetCustomFieldValue`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: CustomFieldSetValueResponse = await response.json();

      if (!data.success) {
        return rejectWithValue(data.message || "Failed to save custom field");
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

export const deleteCustomField = createAsyncThunk<
  DeleteCustomFieldResponse,
  DeleteCustomFieldRequst,
  { rejectValue: string }
>("customField/delete", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}CustomField/DeleteCustomField`,
      {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data: DeleteCustomFieldResponse = await response.json();

    if (!data.success) {
      return rejectWithValue(data.message || "Failed to delete custom field");
    }

    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const updateCustomFieldPositionIdx = createAsyncThunk<
  UpdateCustomFieldPositionIdxResponse,
  UpdateCustomFieldPositionIdxRequest,
  { rejectValue: string }
>(
  "customField/updateCustomFieldPositionIdx",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}CustomField/UpdateCustomFieldPositionIdx`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data: UpdateCustomFieldPositionIdxResponse = await response.json();
      if (!data.success) {
        return rejectWithValue(
          data.message || "Failed to update custom field position",
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

export const GetFieldPurpose = createAsyncThunk<
  GetFieldPurposeResponse,
  GetFieldPurposeRequest,
  { rejectValue: string }
>("customField/getFieldPurpose", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}CustomField/GetFieldPurpose`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data: GetFieldPurposeResponse = await response.json();
    if (!data.success) {
      return rejectWithValue(data.message || "Failed to get field purpose");
    }

    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});
